import { BrowserClient } from 'src/ampli'

export interface Events {
  init: []
  ready: [BrowserClient]
  error: [Error]
}

type EventHandler = (...parameters: any[]) => void

type Message<E extends keyof Events> = {
  event: E
  parameters: Events[E]
}

type EventConsumer = {
  handler: EventHandler
  committed: number
}

type EventHandlerRegistry = {
  [K in keyof Events]: EventConsumer[]
}

type EventStream = {
  events: Array<Message<keyof Events>>
  committed: number
}

export class TypedEventEmitter {
  private stream: EventStream = { events: [], committed: 0 }

  private consumers = {} as EventHandlerRegistry

  private syncConsumer<E extends keyof Events>(
    consumer: EventConsumer,
    event: E
  ) {
    const stream = this.stream

    type MessageWithIndex = Message<E> & { index: number }

    const events = stream.events
      ?.map((event, index) => ({ ...event, index } as MessageWithIndex))
      .filter(({ event: eventType, index }) => {
        return eventType === event && index >= consumer.committed
      })

    events.forEach(({ parameters, index }) => {
      try {
        consumer.handler(...parameters)
        consumer.committed = index
      } catch (error) {
        console.error(`Error in event handler for ${event}`, error)
      }
    })

    consumer.committed = stream.committed
  }

  on<E extends keyof Events>(
    event: E,
    handler: (...parameters: Events[E]) => void,
    offset?: number
  ): this {
    const stream = this.stream
    const consumers = this.consumers[event] || []

    const committed = 'number' === typeof offset ? offset : stream.committed
    const consumer = { handler, committed }
    consumers.push(consumer)
    this.consumers[event] = consumers

    if (stream.committed >= consumer.committed) {
      this.syncConsumer(consumer, event)
    }

    return this
  }

  once<E extends keyof Events>(
    event: E,
    handler: (...parameters: Events[E]) => void,
    offset?: number
  ): this {
    const state = { invoked: false }
    const runOnce = (...parameters: Events[E]) => {
      if (state.invoked) return
      try {
        handler(...parameters)
        state.invoked = true
      } finally {
        this.off(event, runOnce)
      }
    }

    return this.on(event, runOnce, offset)
  }

  off<E extends keyof Events>(
    event: E,
    handler: (...parameters: Events[E]) => void
  ): this {
    const current = this.consumers[event] || []
    const consumers = current.filter(consumer => consumer.handler !== handler)
    this.consumers[event] = consumers
    return this
  }

  emit<E extends keyof Events>(event: E, ...parameters: Events[E]): boolean {
    const stream = this.stream
    const consumers = (this.consumers[event] || [])?.slice()

    stream.events.push({ event, parameters })
    stream.committed = stream.events.length

    const result = { success: 0, failure: 0 }
    for (const consumer of consumers) {
      try {
        consumer.handler(...parameters)
        consumer.committed = stream.committed
        result.success += 1
      } catch (error) {
        result.failure += 1
      }
    }

    return consumers.length > 0 && result.failure === 0
  }
}

export const events = new TypedEventEmitter()
